import React from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { DynamicFormField } from 'components/DynamicFormField/DynamicFormField';
import { AssessmentAuthenticationField } from 'features/assessments/types';
import {
  isValidDateOfBirth,
  isValidAssessmentsEmailAddress,
} from 'utils/inputProcessingUtils/inputProcessingUtils';
import { isValidPhoneNumber } from 'utils/phoneNumberUtils/phoneNumberUtils';
import { ASSESSMENTS_MAX_DATE } from 'features/assessments/constants';
import { isIOSDevice } from 'utils/deviceInfoUtils';

interface AssessmentsAuthenticationFieldComponentProps {
  field: AssessmentAuthenticationField;
  control: Control<FieldValues>;
  externalError?: boolean;
}

export const AssessmentsAuthenticationFieldComponent = ({
  field,
  control,
  externalError = false,
}: AssessmentsAuthenticationFieldComponentProps) => {
  const { key, type, label } = field;

  switch (type) {
    case 'dob':
      return (
        <Controller
          name={key}
          control={control}
          rules={{
            required: `${label} is required`,
            validate: (input: string) => isValidDateOfBirth(input) || `${label} is invalid`,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DynamicFormField
              type="date"
              id={`assessment-auth__${key}`}
              placeholder={label}
              value={value ?? ''}
              label={label}
              onChange={(e) => onChange(e.target.value)}
              error={!!error || externalError}
              helperText={error?.message}
              slotProps={{
                htmlInput: {
                  'aria-label': label,
                  max: ASSESSMENTS_MAX_DATE,
                  ...(isIOSDevice() ? { role: 'textbox' } : {}),
                },
              }}
              autoComplete="off"
            />
          )}
        />
      );
    case 'phone':
      return (
        <Controller
          name={key}
          control={control}
          rules={{
            required: `${label} is required`,
            validate: (input: string) => isValidPhoneNumber(input) || `${label} is invalid`,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DynamicFormField
              type="phone"
              id={`assessment-auth__${key}`}
              label={label}
              placeholder={label}
              value={value ?? ''}
              onChange={onChange}
              error={!!error || externalError}
              helperText={error?.message}
              autoComplete="off"
            />
          )}
        />
      );
    case 'textbox':
    case 'email':
    default:
      return (
        <Controller
          name={key}
          control={control}
          rules={{
            required: `${label} is required`,
            validate: (input: string) =>
              type === 'email'
                ? isValidAssessmentsEmailAddress(input.trim()) || `${label} is invalid`
                : true,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DynamicFormField
              type="textbox"
              id={`assessment-auth__${key}`}
              placeholder={label}
              value={value ?? ''}
              label={label}
              onChange={(e) => onChange(e.target.value)}
              error={!!error || externalError}
              helperText={error?.message}
              autoComplete="off"
              slotProps={{
                htmlInput: {
                  'aria-label': label,
                },
              }}
            />
          )}
        />
      );
  }
};
